/**
 * WizardSidebar - Nested Hierarchical Navigation
 * ===============================================
 * 
 * Features:
 * - Steps with nested sections (sections are children of steps)
 * - Two modes: In Progress (sequential) and Completed (free navigation)
 * - URL deep linking support: ?draft=xxx&step=3&section=transactions
 * - Expand/collapse steps to show/hide sections
 * - Progressive disclosure: only active step expanded in progress mode
 * 
 * @author ClaimSync Team
 * @updated January 2026
 */

import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle, 
  Circle, 
  AlertCircle,
  Building2,
  Calendar,
  FileSpreadsheet,
  Settings,
  DollarSign,
  Calculator,
  FileText,
  ChevronDown,
  ChevronUp,
  Home,
  ArrowLeft,
  Lock,
  Zap,
  Send,
  RefreshCw,
  Upload,
  Eye,
  MessageSquare,
  Sparkles,
  FolderOpen
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Quick Actions configuration by step
const QUICK_ACTIONS: Record<number, { id: string; label: string; icon: React.ComponentType<any>; action?: string }[]> = {
  1: [], // Client Info - no quick actions
  2: [], // Event Details - no quick actions
  3: [
    { id: 'request_clarification', label: 'Request Clarification', icon: MessageSquare, action: 'clarification' },
    { id: 'upload_more', label: 'Upload Documents', icon: Upload, action: 'upload' },
  ],
  4: [], // Additional Details - no quick actions
  5: [
    { id: 'rerun_analysis', label: 'Re-run Analysis', icon: RefreshCw, action: 'rerun' },
    { id: 'case_files', label: 'Case Files', icon: FolderOpen, action: 'files' },
  ],
  6: [
    { id: 'preview_report', label: 'Preview Report', icon: Eye, action: 'preview' },
    { id: 'rerun_analysis', label: 'Re-run Analysis', icon: RefreshCw, action: 'rerun' },
    { id: 'case_files', label: 'Case Files', icon: FolderOpen, action: 'files' },
  ],
  7: [
    { id: 'submit_claim', label: 'Submit Claim', icon: Send, action: 'submit' },
    { id: 'case_files', label: 'Case Files', icon: FolderOpen, action: 'files' },
  ],
};

export interface SidebarSection {
  id: string;
  label: string;
  icon?: React.ReactNode;
  isActive?: boolean;
  isComplete?: boolean;
  badge?: string | number;
  onClick?: () => void;
  hasWarning?: boolean;
  isLocked?: boolean;
}

// Step-to-Section mapping for deep linking
export const STEP_SECTIONS: Record<number, { id: string; label: string }[]> = {
  1: [], // Client Info - no subsections
  2: [], // Event Details - no subsections  
  3: [
    { id: 'upload', label: 'Upload Files' },
    { id: 'summary', label: 'Summary' },
    { id: 'monthly', label: 'Monthly P&L' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'categories', label: 'Categories' },
    { id: 'tax', label: 'Tax Docs' },
    { id: 'reconciliation', label: 'Reconciliation' },
    { id: 'anomalies', label: 'Anomalies' },
  ],
  4: [], // Additional Details - no subsections
  5: [], // Payroll - no subsections
  6: [], // Review - no subsections
  7: [], // Report - no subsections
};

export interface QAItem {
  id: string;
  label: string;
  status: 'missing' | 'warning' | 'complete';
  action?: string;
}

interface WizardSidebarProps {
  currentStep: number;
  highestCompletedStep?: number; // NEW: Track the furthest step the user has reached
  totalSteps: number;
  /**
   * Backend-supplied completion percentage. When undefined the progress
   * UI hides entirely — never render an invented number. SSoT contract:
   * the wizard sidebar is a renderer, not a calculator. See
   * docs/plans/2026-04-25-frontend-calc-drift-audit.md (Fix B).
   */
  completionPercent?: number;
  grade?: 'A' | 'B' | 'C' | 'D' | 'F';
  sections?: SidebarSection[];
  qaItems?: QAItem[];
  nextAction?: {
    label: string;
    description?: string;
    onClick?: () => void;
  };
  calculationMode?: 'standard_bi' | 'future_closure';
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  goToStep?: (step: number) => void;
  onSectionClick?: (stepNumber: number, sectionId: string) => void;
  activeSection?: string;
  // Deep linking support
  urlSection?: string | null;
  // Quick Actions callback
  onQuickAction?: (action: string) => void;
  // QA Assistant callback
  onOpenQAAssistant?: () => void;
}

// Step configuration with icons
const STEP_CONFIG = [
  { step: 1, label: 'Client Info', icon: Building2, hasSubsections: false },
  { step: 2, label: 'Event Details', icon: Calendar, hasSubsections: false },
  { step: 3, label: 'Financial Data', icon: FileSpreadsheet, hasSubsections: true },
  { step: 4, label: 'Additional Details', icon: Settings, hasSubsections: false },
  { step: 5, label: 'Payroll', icon: DollarSign, hasSubsections: false },
  { step: 6, label: 'Review', icon: Calculator, hasSubsections: false },
  { step: 7, label: 'Report', icon: FileText, hasSubsections: false },
];

const GRADE_COLORS = {
  A: 'text-[var(--color-verified)] bg-transparent',
  B: 'text-[var(--color-text-secondary)] bg-[var(--color-bg-elevated)]',
  C: 'text-[var(--color-text-secondary)] bg-[var(--color-bg-elevated)]',
  D: 'text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)]',
  F: 'text-[var(--color-brand)] bg-[var(--color-needs-human-tint)]',
};

export default function WizardSidebar({
  currentStep,
  highestCompletedStep,
  totalSteps,
  completionPercent,
  grade,
  sections = [],
  qaItems = [],
  nextAction,
  calculationMode = 'standard_bi',
  isCollapsed = false,
  onToggleCollapse,
  goToStep,
  onSectionClick,
  activeSection,
  urlSection,
  onQuickAction,
  onOpenQAAssistant,
}: WizardSidebarProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Track expanded steps (in completed mode, multiple can be expanded)
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set([currentStep]));
  
  // Use highestCompletedStep if provided, otherwise fall back to currentStep
  // This allows free navigation to any step the user has already reached
  const maxAccessibleStep = highestCompletedStep ?? currentStep;
  
  // Determine if workflow is complete (100%). Null-safe: when no backend
  // completion is supplied, treat as not-complete (don't pretend).
  const isComplete = (completionPercent ?? 0) >= 100;

  // Count QA items by status
  const missingCount = qaItems.filter(item => item.status === 'missing').length;
  const warningCount = qaItems.filter(item => item.status === 'warning').length;

  // Auto-expand current step when it changes (in progress mode)
  useEffect(() => {
    if (!isComplete) {
      setExpandedSteps(new Set([currentStep]));
    }
  }, [currentStep, isComplete]);

  // Handle URL section parameter for deep linking
  useEffect(() => {
    if (urlSection && currentStep === 3 && onSectionClick) {
      // Find the section and trigger click
      const section = sections.find(s => s.id === urlSection);
      if (section) {
        console.log('🔗 Deep linking to section:', urlSection);
        onSectionClick(currentStep, urlSection);
      }
    }
  }, [urlSection, currentStep, sections, onSectionClick]);

  // Handle step click
  const handleStepClick = (step: number) => {
    console.log(`🔀 handleStepClick: step=${step}, maxAccessibleStep=${maxAccessibleStep}, goToStep=${!!goToStep}`);
    // Always allow navigation to accessible steps (regardless of completion status)
    if (step <= maxAccessibleStep && goToStep) {
      console.log(`🔀 Navigating to step ${step}`);
      goToStep(step);
      setExpandedSteps(new Set([step]));
    } else if (isComplete) {
      // In fully completed mode with no goToStep: toggle expand/collapse for viewing
      console.log(`🔀 Toggling expansion for step ${step}`);
      setExpandedSteps(prev => {
        const next = new Set(prev);
        if (next.has(step)) {
          next.delete(step);
        } else {
          next.add(step);
        }
        return next;
      });
    } else {
      console.log(`🔀 Step ${step} not accessible (maxAccessibleStep=${maxAccessibleStep})`);
    }
  };

  // Handle section click within a step
  const handleSectionClick = (stepNumber: number, section: SidebarSection) => {
    // First navigate to the step if needed
    if (stepNumber !== currentStep && goToStep) {
      goToStep(stepNumber);
    }
    
    // Then activate the section
    if (onSectionClick) {
      onSectionClick(stepNumber, section.id);
    } else if (section.onClick) {
      section.onClick();
    }
  };

  // Check if a step is clickable (accessible)
  const isStepClickable = (step: number) => {
    if (isComplete) return true;
    // User can click any step they've already reached
    return step <= maxAccessibleStep;
  };
  
  // Check if a step has been completed (for showing checkmark)
  const isStepCompleted = (step: number) => {
    // A step is completed if it's before the highest step reached
    // OR if we're viewing a later step (meaning we passed through it)
    return step < maxAccessibleStep || (step < currentStep && step < maxAccessibleStep);
  };

  // Get sections for a specific step
  const getSectionsForStep = (step: number): SidebarSection[] => {
    // Currently, sections are passed as props and are for the current step
    // In a more complete implementation, each step would have its own sections
    if (step === currentStep) {
      return sections;
    }
    return [];
  };

  // Collapsed view
  if (isCollapsed) {
    return (
      <div className="w-16 bg-[var(--color-bg-base)] flex flex-col items-center py-4">
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors mb-4"
          title="Expand sidebar"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        <button
          onClick={() => navigate('/dashboard')}
          className="w-10 h-10 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors mb-6 flex items-center justify-center"
          title="Back to Dashboard"
        >
          <Home className="w-5 h-5" />
        </button>

        <div className="flex flex-col gap-1">
          {STEP_CONFIG.map(({ step, icon: Icon }) => (
            <button
              key={step}
              onClick={() => handleStepClick(step)}
              disabled={!isStepClickable(step)}
              className={`
                w-10 h-10 rounded-lg flex items-center justify-center transition-all
                ${step === currentStep
                  ? 'bg-[var(--color-brand)] text-[var(--color-on-brand)] shadow-lg'
                  : step <= maxAccessibleStep
                    ? 'text-[var(--color-verified)] hover:bg-[var(--color-bg-hover)] cursor-pointer'
                    : 'text-[var(--color-text-dim)] cursor-not-allowed'
                }
              `}
              title={isStepClickable(step) ? `Go to Step ${step}: ${STEP_CONFIG[step-1]?.label}` : `Step ${step}: ${STEP_CONFIG[step-1]?.label} (locked)`}
            >
              {step < maxAccessibleStep && step !== currentStep ? (
                <CheckCircle className="w-[18px] h-[18px]" />
              ) : (
                <Icon className="w-[18px] h-[18px]" />
              )}
            </button>
          ))}
        </div>

        {(missingCount > 0 || warningCount > 0) && (
          <div className="mt-auto mb-4">
            <div className="w-10 h-10 rounded-lg bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] flex items-center justify-center">
              <AlertCircle className="w-5 h-5" />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-60 bg-[var(--color-bg-base)] flex flex-col overflow-hidden">
      {/* Logo Section */}
      <div className="p-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-3" aria-label="The Figure Co.">
          <div className="w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background: 'var(--color-brand)' }}>
            <div className="w-4 h-4 rounded-full" style={{ border: '3px solid var(--color-on-brand)' }} />
          </div>
          <div className="font-serif text-[22px] leading-none" style={{ color: 'var(--color-text-primary)' }}>The Figure Co.</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {/* Status Badge */}
        <div className="px-3 py-2 mb-2">
          <span className={`cs-chip px-2 py-1 rounded ${
            isComplete
              ? 'bg-transparent text-[var(--color-verified)]'
              : 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)]'
          }`}>
            {isComplete ? 'COMPLETED' : 'IN PROGRESS'}
          </span>
        </div>

        {/* PROGRESS Section — only render when backend supplies a number.
            No frontend math allowed (SSoT). */}
        {typeof completionPercent === 'number' && (
          <>
            <p className="px-3 py-2 cs-label">
              Progress
            </p>
            <div className="px-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[var(--color-text-primary)] text-2xl font-bold">{completionPercent}%</span>
                  {grade && (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${GRADE_COLORS[grade]}`}>
                      {grade}
                    </span>
                  )}
                </div>
              </div>
              <div className="h-2 bg-[var(--color-bg-hover)] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    isComplete
                      ? 'bg-[var(--color-verified)]'
                      : 'bg-[var(--color-brand)]'
                  }`}
                  style={{ width: `${completionPercent}%` }}
                />
              </div>
              <p className="text-[var(--color-text-muted)] text-xs mt-1">
                {calculationMode === 'future_closure' ? 'Future Closure Mode' : 'Standard BI Mode'}
              </p>
            </div>
          </>
        )}

        {/* STEPS Section - Hierarchical */}
        <p className="px-3 py-2 cs-label">
          {isComplete ? 'Steps - Click to View' : 'Steps'}
        </p>
        <div className="space-y-1 mb-4">
          {STEP_CONFIG.map(({ step, label, icon: Icon, hasSubsections }) => {
            const isCurrentStep = step === currentStep;
            // A step is "completed" (shows checkmark) if it's before maxAccessibleStep and not the current viewing step
            const isAccessible = step <= maxAccessibleStep;
            const showCheckmark = step < maxAccessibleStep && !isCurrentStep;
            const isExpanded = expandedSteps.has(step);
            const stepSections = step === currentStep ? sections : [];
            const hasSections = stepSections.length > 0;
            const isClickable = isStepClickable(step);

            return (
              <div key={step}>
                {/* Step Button */}
                <button
                  onClick={() => handleStepClick(step)}
                  disabled={!isClickable}
                  className={`
                    w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all
                    ${isCurrentStep
                      ? 'bg-[var(--color-brand)] text-[var(--color-on-brand)] shadow-lg'
                      : isAccessible
                        ? 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] cursor-pointer'
                        : 'text-[var(--color-text-dim)] cursor-not-allowed opacity-60'
                    }
                  `}
                  title={isClickable ? `${isComplete ? 'View' : 'Go to'} Step ${step}` : 'Complete previous steps first'}
                >
                  <div className="flex items-center gap-3">
                    {showCheckmark ? (
                      <CheckCircle className="w-[18px] h-[18px] flex-shrink-0 text-[var(--color-verified)]" />
                    ) : !isClickable ? (
                      <Lock className="w-[18px] h-[18px] flex-shrink-0 text-[var(--color-text-dim)]" />
                    ) : (
                      <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                    )}
                    <span className="font-medium">{label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Step number badge (in progress mode) or expand indicator (completed mode) */}
                    {isCurrentStep && !isComplete && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-bg-elevated)]">
                        {step}/{totalSteps}
                      </span>
                    )}
                    {hasSections && (
                      isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-[var(--color-text-muted)]" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-[var(--color-text-muted)]" />
                      )
                    )}
                  </div>
                </button>

                {/* Nested Sections (only shown when step is expanded) */}
                {hasSections && isExpanded && (
                  <div className="ml-4 mt-1 space-y-0.5 border-l border-[var(--color-border-faint)] pl-2">
                    {stepSections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => handleSectionClick(step, section)}
                        className={`
                          w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all
                          ${section.isActive
                            ? 'bg-[var(--color-needs-human-tint)] text-[var(--color-text-primary)]'
                            : 'text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]'
                          }
                        `}
                      >
                        <div className="flex items-center gap-2">
                          {section.isComplete ? (
                            <CheckCircle className="w-3.5 h-3.5 text-[var(--color-verified)]" />
                          ) : section.hasWarning ? (
                            <AlertCircle className="w-3.5 h-3.5 text-[var(--color-brand)]" />
                          ) : (
                            <Circle className="w-3.5 h-3.5 text-[var(--color-text-dim)]" />
                          )}
                          <span>{section.label}</span>
                        </div>
                        {section.badge && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            section.isActive ? 'bg-[var(--color-bg-elevated)]' : 'bg-[var(--color-bg-hover)]'
                          }`}>
                            {section.badge}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* QA ASSISTANT Section */}
        <button
          onClick={onOpenQAAssistant}
          className="w-full px-3 py-2 flex items-center justify-between cs-label hover:bg-[var(--color-bg-hover)] transition rounded-lg group"
        >
          <span className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[var(--color-brand)]" />
            QA Assistant
          </span>
          <span className="text-xs normal-case font-normal text-[var(--color-text-muted)] group-hover:text-[var(--color-text-primary)]/70">
            Click to chat →
          </span>
        </button>
        <div className="px-3">
          {missingCount > 0 && (
            <div className="mb-3">
              <div className="flex items-center gap-2 text-[var(--color-brand)] text-xs font-medium mb-2">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>Missing ({missingCount})</span>
              </div>
              <div className="space-y-1 pl-1">
                {qaItems
                  .filter(item => item.status === 'missing')
                  .map(item => (
                    <div
                      key={item.id}
                      className="text-xs text-[var(--color-text-muted)] flex items-center gap-2"
                    >
                      <span className="text-[var(--color-brand)]">•</span>
                      {item.label}
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {warningCount > 0 && (
            <div className="mb-3">
              <div className="flex items-center gap-2 text-[var(--color-brand)] text-xs font-medium mb-2">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>Issues ({warningCount})</span>
              </div>
              <div className="space-y-1 pl-1">
                {qaItems
                  .filter(item => item.status === 'warning')
                  .map(item => (
                    <div
                      key={item.id}
                      className="text-xs text-[var(--color-text-muted)] flex items-center gap-2"
                    >
                      <span className="text-[var(--color-brand)]">•</span>
                      {item.label}
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {missingCount === 0 && warningCount === 0 && qaItems.length > 0 && (
            <div className="flex items-center gap-2 text-[var(--color-verified)] text-xs font-medium mb-3">
              <CheckCircle className="w-3.5 h-3.5" />
              <span>All checks passed!</span>
            </div>
          )}

          {/* Quick Actions Section */}
          {QUICK_ACTIONS[currentStep]?.length > 0 && (
            <div className="mb-4 pt-3 border-t border-[var(--color-border-faint)]">
              <div className="flex items-center gap-2 text-[var(--color-text-secondary)] text-xs font-semibold mb-2 uppercase tracking-wider">
                <Zap className="w-3.5 h-3.5 text-[var(--color-brand)]" />
                <span>Quick Actions</span>
              </div>
              <div className="space-y-1.5">
                {QUICK_ACTIONS[currentStep].map((action) => {
                  const ActionIcon = action.icon;
                  return (
                    <button
                      key={action.id}
                      onClick={() => onQuickAction?.(action.action || action.id)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm 
                        text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] bg-[var(--color-bg-hover)] hover:bg-[var(--color-bg-hover)] 
                        transition-all border border-[var(--color-border-faint)] hover:border-[var(--color-border)]"
                    >
                      <ActionIcon className="w-4 h-4 text-[var(--color-brand)]" />
                      <span>{action.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {nextAction && (
            <div className="mt-3 pt-3 border-t border-[var(--color-border-faint)]">
              <div className="text-xs text-[var(--color-text-muted)] mb-2">→ Next Action</div>
              <button
                onClick={nextAction.onClick}
                className="w-full p-3 bg-[var(--color-needs-human-tint)] hover:bg-[var(--color-needs-human-tint-bd)] rounded-lg text-left transition-all border border-[var(--color-needs-human-tint-bd)]"
              >
                <div className="text-sm text-[var(--color-text-primary)] font-medium">{nextAction.label}</div>
                {nextAction.description && (
                  <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{nextAction.description}</div>
                )}
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Bottom section */}
      <div className="p-3 border-t border-[var(--color-border-faint)] space-y-1">
        <button
          onClick={() => navigate('/dashboard')}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] transition-all"
        >
          <ArrowLeft className="w-[18px] h-[18px] flex-shrink-0" />
          <span className="font-medium">Back to Dashboard</span>
        </button>
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] transition-all"
        >
          <ChevronLeft className="w-[18px] h-[18px] flex-shrink-0" />
          <span className="font-medium">Collapse</span>
        </button>
      </div>
    </div>
  );
}
